import _ from 'lodash';
import {FemaGoModal, TextBoxField} from 'app-ui-componentlibrary';
import {
  ENTITY_MAP,
  ENTITY_MAP_PLURAL,
  ROLE_MAP_INTERNAL,
} from 'go/components/organization/team/constants';
import React, {useEffect, useState, useRef} from 'react';
import {
  Button,
  Card,
  Divider,
  Dropdown,
  Form,
  Grid,
  Icon,
  Loader,
  Message,
} from 'semantic-ui-react';
import UserContactInformation from './UserContactInformation';
import {RolesAndPermissionsMessage} from './RolesAndPermissionsPopup';
import REST from 'go/requests/rest';
import SignatureTitleForm from './SignatureTitleForm';
import {forceJsonResponse, MONO, FLUX} from 'common/utils';

const ManageTeamMemberModal = ({
  isOpen,
  toggleModal,
  signatureTitleInfo,
  userToManage,
  setUserToManage,
  eligibleRoleIds,
  fetchUserData,
}) => {
  const [entityType, setEntityType] = useState(null);
  const [role, setRole] = useState(null);
  const [entities, setEntities] = useState([]);
  const [addingRole, setAddingRole] = useState(false);
  const [roles, setRoles] = useState([]);
  const [editingRole, setEditingRole] = useState(false);
  const [editRoleIndex, setEditRoleIndex] = useState(null);
  const [dropdownMap, setDropdownMap] = useState({});
  const [dropdownMapLoading, setDropdownMapLoading] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteRoleIndex, setDeleteRoleIndex] = useState(null);
  const [title1, setTitle1] = useState(_.get(signatureTitleInfo, 'titles[0]'));
  const [title2, setTitle2] = useState(_.get(signatureTitleInfo, 'titles[1]'));
  const [signatureFileType, setSignatureFileType] = useState(
    _.get(signatureTitleInfo, 'fileType')
  );
  const [signatureBase64Data, setSignatureBase64Data] = useState(
    _.get(signatureTitleInfo, 'signatureImage')
  );
  const [signatureTitleInfoToBePopulated, setSignatureTitleInfoToBePopulated] = useState(
    {}
  );

  const ASA_ROLE_IDS = [8, 31, 32, 33, 34, 35, 36, 37, 38, 39, 40];
  const DeOBA_ROLE_IDS = [50, 51, 52, 53, 54, 55, 56, 57, 58, 59, 60];

  const resetSignatureTitleInfoToBePopulated = () => {
    const getTempOrOriginalSignatureTitleInfo = (thing) =>
      _.get(signatureTitleInfoToBePopulated, thing, _.get(signatureTitleInfo, thing));
    const newSignatureTitleInfo = {
      signatureFileType: getTempOrOriginalSignatureTitleInfo('signatureFileType'),
      signatureBase64Data: getTempOrOriginalSignatureTitleInfo('signatureBase64Data'),
      title1: getTempOrOriginalSignatureTitleInfo('title1'),
      title2: getTempOrOriginalSignatureTitleInfo('title2'),
    };

    setSignatureFileType(newSignatureTitleInfo.signatureFileType);
    setSignatureBase64Data(newSignatureTitleInfo.signatureBase64Data);
    setTitle1(newSignatureTitleInfo.title1);
    setTitle2(newSignatureTitleInfo.title2);

    return newSignatureTitleInfo;
  };

  const setAndReturnSignatureTitleInfoToBePopulated = (signatureTitleInfo) => {
    setSignatureTitleInfoToBePopulated(signatureTitleInfo);
    return signatureTitleInfo;
  };

  const resetAssignmentFormState = () => {
    setEntityType(null);
    setRole(null);
    setEntities([]);
  };

  const resetEditingState = () => {
    setEditingRole(false);
    setEditRoleIndex(null);
  };

  useEffect(() => {
    setDropdownMap(_.get(userToManage, 'assignable.dropdownMap', {}));
    setRoles(_.map(_.get(userToManage, 'roles', []), (r, index) => ({...r, index})));
  }, [_.get(userToManage, 'emailAdr')]);

  useEffect(() => {
    setSignatureFileType(_.get(signatureTitleInfo, 'fileType'));
    setSignatureBase64Data(_.get(signatureTitleInfo, 'signatureImage'));
    setTitle1(_.get(signatureTitleInfo, 'titles[0]', ''));
    setTitle2(_.get(signatureTitleInfo, 'titles[1]', ''));
    setSignatureTitleInfoToBePopulated({});
  }, [_.get(signatureTitleInfo, 'emailAdr')]);

  const fetchNewDropdowns = async (permissions) => {
    const res = await REST.user.searchAssignablePermissions(permissions);
    const newDropdowns = await forceJsonResponse(res, MONO);
    const dropdownMap = _.get(newDropdowns, 'dropdownMap');
    setDropdownMap(dropdownMap);
    setDropdownMapLoading(false);
  };

  const needsSignatureTitleInfo = () => {
    return (
      _.includes(ASA_ROLE_IDS, _.toNumber(role)) ||
      _.includes(DeOBA_ROLE_IDS, _.toNumber(role))
    );
  };
  const needsSignature = () => {
    return _.includes(ASA_ROLE_IDS, _.toNumber(role));
  };
  const addRole = (entityTypeId, roleId, entities, dropdownMap, index) => {
    const roleEntitiesToAdd = _.map(entities, (e) => {
      const dropdownEntity = _.find(
        _.get(dropdownMap, `${entityTypeId}.${roleId}`),
        (entity) => entity.entityId === e
      );
      return {
        entityId: e,
        entityName: dropdownEntity.entityName,
        canManage: true,
      };
    });

    const newSignatureTitleInfo =
      _.includes(ASA_ROLE_IDS, _.toNumber(role)) ||
      _.includes(DeOBA_ROLE_IDS, _.toNumber(role))
        ? setAndReturnSignatureTitleInfoToBePopulated({
            signatureBase64Data,
            signatureFileType,
            title1,
            title2,
          })
        : resetSignatureTitleInfoToBePopulated();

    const matchingRole = _.find(roles, (r) => _.toNumber(r.index) === _.toNumber(index));
    const roleEntitiesCantManage = _.filter(
      _.get(matchingRole, 'entities'),
      (e) => !e.canManage
    );

    const otherRoles = _.filter(roles, (r) => _.toNumber(r.index) !== _.toNumber(index));

    const newRole = {
      index,
      roleId,
      entityTypeId,
      entities: [...roleEntitiesCantManage, ...roleEntitiesToAdd],
    };

    const newRoles = [...otherRoles, newRole];
    setRoles(newRoles);
    return {newRoles, newSignatureTitleInfo};
  };

  const deleteRole = (entityTypeId, roleId, entities) => {
    const matchingRole = _.find(
      roles,
      (r) =>
        _.toNumber(r.roleId) === _.toNumber(roleId) &&
        _.toNumber(r.entityTypeId) === _.toNumber(entityTypeId)
    );
    const otherRoles = _.filter(
      roles,
      (r) =>
        !(
          _.toNumber(r.roleId) === _.toNumber(roleId) &&
          _.toNumber(r.entityTypeId) === _.toNumber(entityTypeId)
        )
    );
    const leftoverRole = {
      index: _.get(matchingRole, 'index', otherRoles.length),
      roleId,
      entityTypeId,
      entities: _.filter(matchingRole.entities, (e) => !_.includes(entities, e.entityId)),
    };
    const leftoverRoleNeedsToBeDeleted = _.get(leftoverRole, 'entities.length') > 0;
    const newRoles = leftoverRoleNeedsToBeDeleted
      ? [...otherRoles, leftoverRole]
      : _.map(_.orderBy(otherRoles, 'index', 'asc'), (r, i) => ({...r, index: i}));
    if (leftoverRoleNeedsToBeDeleted && deleteRoleIndex < editRoleIndex)
      setEditRoleIndex(editRoleIndex - 1);
    setRoles(newRoles);
    return newRoles;
  };

  const validationError = (validation) => {
    if (dropdownMapLoading) return <></>;

    const messages = {
      FORBIDDEN: `You cannot add this user as a ${ROLE_MAP_INTERNAL[role]} for the ${
        ENTITY_MAP[entityType]
      } "${validation.entityName}" because the user is already a ${
        ROLE_MAP_INTERNAL[_.get(validation, 'otherTriplet.roleId')]
      } for the ${ENTITY_MAP[_.get(validation, 'otherTriplet.entityTypeId')]} "${_.get(
        validation,
        'otherTriplet.entityName'
      )}."`,
      DUPLICATE: `You cannot add this user as a ${ROLE_MAP_INTERNAL[role]} for the ${ENTITY_MAP[entityType]} "${validation.entityName}" because the user already has this role.`,
    };
    return <Message negative>{_.get(messages, validation.reason)}</Message>;
  };

  const currentSelections = _.filter(_.get(dropdownMap, `${entityType}.${role}`), (e) =>
    _.includes(entities, e.entityId)
  );
  const validations = _.flatMap(
    _.map(currentSelections, (c) =>
      _.map(c.validations, (v) => ({...v, entityName: c.entityName}))
    )
  );
  const addOrEditInProgress = roles.length === 0 || addingRole || editingRole;
  const formNotFilledOut = !entityType || !role || !entities.length;
  const deletedAllRolesForUser =
    _.get(userToManage, 'roles.length') > 0 && roles.length === 0;

  const noChangesMade =
    !addOrEditInProgress &&
    _.isEqual(
      userToManage.roles,
      _.map(roles, (r) => _.omit(r, 'index'))
    );
  const missingOrInvalidSignatureTitleInfo =
    needsSignatureTitleInfo() &&
    (_.isEmpty(title1) ||
      _.isEmpty(title2) ||
      _.size(title1) > 200 ||
      _.size(title2) > 200 ||
      (ASA_ROLE_IDS.includes(_.toNumber(role)) &&
        (_.isEmpty(signatureFileType) || _.isEmpty(signatureBase64Data))));
  const disableButtons =
    (addOrEditInProgress && formNotFilledOut && !deletedAllRolesForUser) ||
    (!dropdownMapLoading && validations.length > 0) ||
    missingOrInvalidSignatureTitleInfo;

  /*
   * The semantic-ui-react Dropdown component is not 508 compliant.  ANDI tool complains about "Elements with No Accessible Name".
   * The Refs below are assigned to the "ref" attribute of each of the Dropdown components so that we may update the rendered content
   * with an aria-label value to make them 508 compliant.
   */
  const categorySelectRef = useRef(null);
  if (categorySelectRef.current)
    categorySelectRef.current
      .querySelector('input')
      .setAttribute('aria-label', 'Category');
  const roleSelectRef = useRef(null);
  if (roleSelectRef.current)
    roleSelectRef.current.querySelector('input').setAttribute('aria-label', 'Role');
  const entitiesSelectRef = useRef(null);
  if (entitiesSelectRef.current)
    entitiesSelectRef.current
      .querySelector('input')
      .setAttribute('aria-label', 'Assigned for');

  const renderAssignmentForm = (index) => (
    <Card fluid className="roles-container">
      <Grid fluid>
        <Grid.Column width={13}>
          <strong>{`Role ${(index !== undefined ? index : roles.length) + 1}`}</strong>
        </Grid.Column>
        <Grid.Column width={3}>
          {roles.length > 0 && (
            <span
              onClick={(e) => {
                e.preventDefault();
                resetAssignmentFormState();
                setAddingRole(false);
                resetEditingState();
                resetSignatureTitleInfoToBePopulated();
              }}
            >
              <Icon className="action-icon" name="delete" color="blue" />
              <a title="Cancel" className="deleteIcon" color="blue" tabIndex={0} href="#">
                Cancel
              </a>
            </span>
          )}
        </Grid.Column>
      </Grid>
      <Form
        style={{
          marginTop: '10px',
        }}
      >
        {validations.length > 0 && _.map(validations, validationError)}
        <Form.Field>
          <label htmlFor="categorySelect">
            <strong>Category</strong>
          </label>
          <div ref={categorySelectRef}>
            <Dropdown
              id="categorySelect"
              name="categorySelect"
              fluid
              placeholder={'...'}
              search
              selection
              value={entityType}
              onChange={(e, {value}) => {
                setRole(null);
                setEntityType(value);
                if (_.toNumber(value) === 11) {
                  setEntities([0]);
                }
              }}
              options={_.orderBy(
                _.map(_.keys(dropdownMap), (entityTypeId) => ({
                  key: `level${entityTypeId}`,
                  value: entityTypeId,
                  text: ENTITY_MAP[entityTypeId],
                })),
                'text',
                'asc'
              )}
            />
          </div>
        </Form.Field>
        <Form.Field>
          <label htmlFor="roleSelect">
            <strong>Role</strong>
          </label>
          <div ref={roleSelectRef}>
            <Dropdown
              id="roleSelect"
              name="roleSelect"
              fluid
              placeholder={'...'}
              search
              selection
              value={role}
              onChange={(e, {value}) => {
                if (_.toNumber(entityType) !== 11) {
                  setEntities([]);
                }
                setRole(value);
              }}
              options={_.orderBy(
                _.map(_.keys(_.get(dropdownMap, entityType)), (roleId) => ({
                  key: `role${roleId}`,
                  value: roleId,
                  text: ROLE_MAP_INTERNAL[roleId],
                })),
                'text',
                'asc'
              )}
            />
          </div>
        </Form.Field>
        {needsSignatureTitleInfo() && (
          <SignatureTitleForm
            signatureTitleInfo={signatureTitleInfo}
            needSignature={needsSignature()}
            signatureFileType={signatureFileType}
            setSignatureFileType={setSignatureFileType}
            signatureBase64Data={signatureBase64Data}
            setSignatureBase64Data={setSignatureBase64Data}
            title1={title1}
            title2={title2}
            setTitle1={setTitle1}
            setTitle2={setTitle2}
          />
        )}
        {_.toNumber(entityType) !== 11 && (
          <Form.Field>
            <label htmlFor="entitiesSelect">
              <strong>Assigned for</strong>
            </label>
            <div ref={entitiesSelectRef}>
              <Dropdown
                id="entitiesSelect"
                name="entitiesSelect"
                fluid
                placeholder={'...'}
                multiple
                search
                selection
                value={entities}
                onChange={(e, {value}) => {
                  setEntities(value);
                }}
                options={_.orderBy(
                  _.map(_.get(dropdownMap, `${entityType}.${role}`), (entity) => ({
                    key: `entity${entity.entityId}`,
                    value: entity.entityId,
                    text: entity.entityName,
                    content: (
                      <span aria-label={entity.entityName}>{entity.entityName}</span>
                    ),
                  })),
                  'text',
                  'asc'
                )}
              />
            </div>
          </Form.Field>
        )}
      </Form>
    </Card>
  );

  const renderDeleteConfirmationModal = (index) => {
    const currentRole = _.find(roles, (r) => r.index === index);

    if (!currentRole) return null;

    const entitiesCanManage = _.filter(currentRole.entities, (e) => e.canManage);

    return (
      <FemaGoModal
        title="Delete role"
        open={deleteModalOpen}
        size="small"
        buttonText="Delete"
        type="alert"
        cancelAction={() => {
          setDeleteModalOpen(false);
          setDeleteRoleIndex(null);
        }}
        confirmAction={() => {
          const entityIds = _.map(entitiesCanManage, (e) => e.entityId);
          const currentRoles = deleteRole(
            currentRole.entityTypeId,
            currentRole.roleId,
            entityIds
          );
          const permissions = _.flatMap(
            _.map(currentRoles, (role) =>
              _.map(
                role.entities,
                (entity) => `${role.entityTypeId}:${entity.entityId}:${role.roleId}`
              )
            )
          );
          fetchNewDropdowns(permissions);
          setDeleteModalOpen(false);
          setDeleteRoleIndex(null);
        }}
      >
        Are you sure you want to delete this role?
        <Card fluid>
          <Grid.Column width={13}>
            <Grid celled="internally" fluid row={2}>
              <Grid.Column>
                <strong>{`Role ${index + 1}`}</strong>
                <div>{ROLE_MAP_INTERNAL[currentRole.roleId]}</div>
                <hr />
                <strong>
                  {entitiesCanManage.length > 1
                    ? ENTITY_MAP_PLURAL[currentRole.entityTypeId]
                    : ENTITY_MAP[currentRole.entityTypeId]}
                </strong>
                <div>{_.map(entitiesCanManage, (e) => e.entityName).join(', ')}</div>
              </Grid.Column>
            </Grid>
          </Grid.Column>
        </Card>
      </FemaGoModal>
    );
  };

  return (
    <>
      {renderDeleteConfirmationModal(deleteRoleIndex)}
      <FemaGoModal
        title="Manage this team member"
        open={isOpen}
        size="small"
        confirmButton="hidden"
        cancelAction={() => {
          setUserToManage({});
          setAddingRole(false);
          resetEditingState();
          resetAssignmentFormState();
          toggleModal();
        }}
      >
        {_.get(userToManage, 'emailAdr') ? (
          <div id="org-team-form">
            <p>Review and update roles assigned to this member of your team.</p>
            <UserContactInformation user={userToManage} />
            <strong>Assign roles</strong>
            <p>Add, edit, or delete this user's role(s).</p>
            {_.map(_.orderBy(roles, 'index', 'asc'), (currentRole) => {
              const index = currentRole.index;

              if (editingRole && editRoleIndex === index)
                return renderAssignmentForm(index);

              return (
                <Card fluid key={'role' + index} id="roleProgram-details">
                  <Grid celled="internally" fluid>
                    <Grid.Column width={13} className="left-side">
                      <Grid celled="internally" fluid row={2}>
                        <Grid.Column>
                          <strong>{`Role ${index + 1}`}</strong>
                          <div>{ROLE_MAP_INTERNAL[currentRole.roleId]}</div>
                          <hr />
                          <strong>
                            {currentRole.entities.length > 1
                              ? ENTITY_MAP_PLURAL[currentRole.entityTypeId]
                              : ENTITY_MAP[currentRole.entityTypeId]}
                          </strong>
                          <div>
                            {_.map(currentRole.entities, (e) => e.entityName).join(', ')}
                          </div>
                        </Grid.Column>
                      </Grid>
                    </Grid.Column>
                    <Grid.Column width={3} className="right-side">
                      <Grid columns={16}>
                        {_.some(currentRole.entities, (e) => e.canManage) && (
                          <Grid.Column width={16}>
                            <Button
                              name={`editRole${index}`}
                              primary
                              basic
                              fluid
                              disabled={disableButtons}
                              onClick={(e) => {
                                e.preventDefault();
                                const indexToUse = editingRole
                                  ? editRoleIndex
                                  : roles.length;
                                const {newRoles: currentRoles} =
                                  addOrEditInProgress && !formNotFilledOut
                                    ? addRole(
                                        entityType,
                                        role,
                                        entities,
                                        dropdownMap,
                                        indexToUse
                                      )
                                    : {newRoles: roles};
                                setAddingRole(false);
                                setEditingRole(true);
                                setEditRoleIndex(index);
                                setRole(`${currentRole.roleId}`);
                                setEntityType(`${currentRole.entityTypeId}`);
                                const entitiesCanManage = _.filter(
                                  currentRole.entities,
                                  (e) => e.canManage
                                );
                                setEntities(_.map(entitiesCanManage, (e) => e.entityId));
                                const entitiesToTriplets = _.map(
                                  entitiesCanManage,
                                  (e) =>
                                    `${currentRole.entityTypeId}:${e.entityId}:${currentRole.roleId}`
                                );
                                const permissions = _.flatMap(
                                  _.map(currentRoles, (role) =>
                                    _.map(
                                      role.entities,
                                      (entity) =>
                                        `${role.entityTypeId}:${entity.entityId}:${role.roleId}`
                                    )
                                  )
                                );
                                const newPermissions = _.filter(
                                  permissions,
                                  (permission) =>
                                    !_.includes(entitiesToTriplets, permission)
                                );
                                setDropdownMapLoading(true);
                                fetchNewDropdowns(newPermissions);
                              }}
                            >
                              <Icon className="action-icon" name="pencil" color="blue" />
                              {'Edit'}
                            </Button>
                            <Divider />
                            <Button
                              name={`deleteRole${index}`}
                              negative
                              basic
                              fluid
                              onClick={(e) => {
                                e.preventDefault();
                                setDeleteModalOpen(true);
                                setDeleteRoleIndex(index);
                              }}
                            >
                              <Icon className="action-icon" name="delete" color="red" />
                              {'Delete'}
                            </Button>
                          </Grid.Column>
                        )}
                      </Grid>
                    </Grid.Column>
                  </Grid>
                </Card>
              );
            })}
            {(roles.length === 0 || addingRole) && renderAssignmentForm()}
            <Divider className="hr-dotted" />
            <Button
              primary
              basic
              id="addRole"
              name="addRole"
              onClick={(e) => {
                e.preventDefault();
                const indexToUse = editingRole ? editRoleIndex : roles.length;
                const {newRoles: currentRoles} =
                  addOrEditInProgress && !formNotFilledOut
                    ? addRole(entityType, role, entities, dropdownMap, indexToUse)
                    : {newRoles: roles};
                const permissions = _.flatMap(
                  _.map(currentRoles, (role) =>
                    _.map(
                      role.entities,
                      (entity) => `${role.entityTypeId}:${entity.entityId}:${role.roleId}`
                    )
                  )
                );
                fetchNewDropdowns(permissions);
                resetEditingState();
                resetAssignmentFormState();
                setAddingRole(true);
              }}
              disabled={disableButtons}
            >
              <Icon name="plus" />
              {'Add another role'}
            </Button>
            <RolesAndPermissionsMessage eligibleRoleIds={eligibleRoleIds} />
            <Button
              primary
              compact
              id="saveAndClose"
              name="saveAndClose"
              onClick={async () => {
                const originalPermissions = _.flatMap(
                  _.map(_.get(userToManage, 'roles'), (role) =>
                    _.map(
                      role.entities,
                      (entity) => `${role.entityTypeId}:${entity.entityId}:${role.roleId}`
                    )
                  )
                );

                const indexToUse = editingRole ? editRoleIndex : roles.length;
                const {newRoles: currentRoles, newSignatureTitleInfo} =
                  addOrEditInProgress && !formNotFilledOut
                    ? addRole(entityType, role, entities, dropdownMap, indexToUse)
                    : {
                        newSignatureTitleInfo: signatureTitleInfoToBePopulated,
                        newRoles: roles,
                      };
                const currentPermissions = _.flatMap(
                  _.map(currentRoles, (role) =>
                    _.map(
                      role.entities,
                      (entity) => `${role.entityTypeId}:${entity.entityId}:${role.roleId}`
                    )
                  )
                );

                const addedPermissions = _.difference(
                  currentPermissions,
                  originalPermissions
                );
                const deletedPermissions = _.difference(
                  originalPermissions,
                  currentPermissions
                );

                const permissionToUER = (permission) => {
                  const [entityTypeId, entityId, roleId] = _.split(permission, ':');
                  return {
                    entityTypeId,
                    entityId,
                    roleId,
                  };
                };

                const toAdd = _.map(addedPermissions, permissionToUER);
                const toDelete = _.map(deletedPermissions, permissionToUER);

                await REST.user.manageUserRoles(userToManage.emailAdr, toAdd, toDelete);

                if (newSignatureTitleInfo != {}) {
                  const body = {
                    emailAdr: _.get(userToManage, 'emailAdr'),
                    fileType: _.get(newSignatureTitleInfo, 'signatureFileType'),
                    signatureImage: _.get(newSignatureTitleInfo, 'signatureBase64Data'),
                    titles: [
                      _.get(newSignatureTitleInfo, 'title1'),
                      _.get(newSignatureTitleInfo, 'title2'),
                    ],
                  };
                  await REST.user.modifySignature(body);
                }

                fetchUserData();
                setUserToManage({});
                resetEditingState();
                resetAssignmentFormState();
                setAddingRole(false);
                toggleModal();
              }}
              disabled={disableButtons || noChangesMade}
            >
              Save &amp; close
              <Icon name="check" size="small" />
            </Button>
          </div>
        ) : (
          <Loader active inline="centered" className="workaround">
            Loading...
          </Loader>
        )}
      </FemaGoModal>
    </>
  );
};

export default ManageTeamMemberModal;
